import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Trash2, Plus, Send, CheckCheck, Clock, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newType, setNewType] = useState('org');
  const [sending, setSending] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendToAll, setSendToAll] = useState(true);
  const [error, setError] = useState('');
  const countRequest = useRef(0), listRequest = useRef(0);
  const dropdownRef = useRef(null);
  const { user } = useAuth();

  const isSuperadmin = user?.is_superadmin === true;
  const isManagement = user?.role === 'management';

  const fetchUnread = useCallback(async () => {
    const request = ++countRequest.current;
    try {
      const res = await axios.get(`${API}/announcements/unread-count`);
      if (request === countRequest.current) setUnreadCount(res.data.unread_count);
    } catch { /* silent */ }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    const count = ++countRequest.current, list = ++listRequest.current;
    try {
      const [res, unread] = await Promise.all([axios.get(`${API}/announcements`), axios.get(`${API}/announcements/unread-count`)]);
      if (list === listRequest.current) { setAnnouncements(res.data); setError(''); }
      if (count === countRequest.current) setUnreadCount(unread.data.unread_count);
    } catch { if (list === listRequest.current) setError('Unable to load notifications'); }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchAnnouncements();
  }, [open, fetchAnnouncements]);
  useEffect(() => {
    const refresh = () => open ? fetchAnnouncements() : fetchUnread();
    window.addEventListener('notification-preferences-changed', refresh);
    return () => window.removeEventListener('notification-preferences-changed', refresh);
  }, [open, fetchAnnouncements, fetchUnread]);

  useEffect(() => {
    if (showCreate && orgUsers.length === 0) {
      axios.get(`${API}/org-users`).then(res => setOrgUsers(res.data)).catch(() => {});
    }
  }, [showCreate, orgUsers.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setShowCreate(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/announcements/${id}/read`);
      listRequest.current += 1;
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
      fetchUnread();
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/announcements/read-all`);
      listRequest.current += 1;
      countRequest.current += 1;
      setAnnouncements(prev => prev.map(a => ({ ...a, is_read: true })));
      setUnreadCount(0);
      fetchAnnouncements();
      toast.success('All marked as read');
    } catch { /* silent */ }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    if (newType === 'org' && !sendToAll && !selectedRecipients.length) return toast.error('Select at least one recipient');
    setSending(true);
    try {
      await axios.post(`${API}/announcements`, {
        type: newType,
        title: newTitle.trim(),
        message: newMessage.trim(),
        recipient_ids: sendToAll ? [] : selectedRecipients
      });
      toast.success('Notification sent');
      setShowCreate(false);
      setNewTitle('');
      setNewMessage('');
      setNewType('org');
      setSelectedRecipients([]);
      setSendToAll(true);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`);
      listRequest.current += 1;
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      fetchUnread();
      toast.success('Announcement deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const d = new Date(dateStr);
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-md hover:bg-slate-100 transition-colors"
        data-testid="bell-button"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={20} className="text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 flex items-center justify-center min-w-[18px] px-1" data-testid="unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-4 right-4 top-20 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <p className="text-sm font-bold text-slate-900">Notifications</p>
            <div className="flex items-center gap-2">
              {isManagement && <button type="button" onClick={() => { setOpen(false); navigate('/settings/notifications'); }} aria-label="My notification preferences" title="My notification preferences" className="p-1 text-slate-500 hover:text-slate-900" data-testid="notification-preferences-link"><Settings size={15} /></button>}
              {unreadCount > 0 && (
                <button type="button" onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="mark-all-read">
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCreate(!showCreate)}
                className="text-xs bg-slate-900 text-white px-2 py-1 rounded hover:bg-slate-800 flex items-center gap-1"
                data-testid="new-announcement-btn"
              >
                <Plus size={12} /> New
              </button>
            </div>
          </div>

          {showCreate && (
            <form onSubmit={handleCreate} className="p-3 border-b border-slate-200 bg-slate-50 space-y-2" data-testid="create-announcement-form">
              {isSuperadmin && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewType('org')}
                    data-testid="org-announcement-type-button"
                    className={`text-xs px-2 py-1 rounded ${newType === 'org' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600'}`}
                  >
                    My Org
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewType('platform')}
                    className={`text-xs px-2 py-1 rounded ${newType === 'platform' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}
                    data-testid="platform-type-btn"
                  >
                    Platform-Wide
                  </button>
                </div>
              )}
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Title (optional)"
                className="text-sm h-8"
                data-testid="announcement-title-input"
              />
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Message..."
                required
                rows={2}
                className="text-sm resize-none"
                data-testid="announcement-message-input"
              />
              {newType === 'org' && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setSendToAll(true); setSelectedRecipients([]); }}
                      data-testid="announcement-entire-org-button"
                      className={`text-xs px-2 py-1 rounded ${sendToAll ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600'}`}
                    >
                      Entire Org
                    </button>
                    <button
                      type="button"
                      onClick={() => setSendToAll(false)}
                      className={`text-xs px-2 py-1 rounded ${!sendToAll ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600'}`}
                      data-testid="specific-people-btn"
                    >
                      Specific People
                    </button>
                  </div>
                  {!sendToAll && (
                    <div className="max-h-24 overflow-y-auto space-y-1 border border-slate-200 rounded p-1.5" data-testid="recipient-list">
                      {orgUsers.filter(u => u.id !== user?.id).map(u => (
                        <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            data-testid={`announcement-recipient-${u.id}`}
                            checked={selectedRecipients.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedRecipients([...selectedRecipients, u.id]);
                              else setSelectedRecipients(selectedRecipients.filter(id => id !== u.id));
                            }}
                            className="rounded"
                          />
                          <span className="text-slate-700">{u.name}</span>
                          <span className="text-slate-400 capitalize">({u.role})</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button type="submit" size="sm" className="w-full h-8 text-xs" disabled={sending || !newMessage.trim() || (newType === 'org' && !sendToAll && !selectedRecipients.length)} data-testid="post-announcement-btn">
                <Send size={12} className="mr-1" /> {sending ? 'Posting...' : 'Post Announcement'}
              </Button>
            </form>
          )}

          <div className="max-h-80 overflow-y-auto" data-testid="announcements-list">
            {error ? <div className="p-4 space-y-2 text-sm" role="alert" data-testid="notification-load-error"><p>{error}</p><Button variant="outline" size="sm" onClick={fetchAnnouncements} data-testid="notification-load-retry">Retry</Button></div> : announcements.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400" data-testid="notification-empty">No notifications</div>
            ) : (
              announcements.map(ann => (
                <div
                  key={ann.id}
                  className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!ann.is_read ? 'bg-blue-50/50' : ''} ${ann.system_kind === 'deadline' ? 'border-l-4 border-l-amber-400' : ''}`}
                  onClick={() => {
                    if (!ann.is_read) markRead(ann.id);
                    if (ann.system_kind === 'deadline' && ann.job_id) {
                      setOpen(false);
                      navigate(`/jobs/${ann.job_id}`);
                    }
                  }}
                  data-testid={`announcement-${ann.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {!ann.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                        {ann.type === 'platform' && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold uppercase">Platform</span>
                        )}
                        {ann.system_kind === 'deadline' && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold uppercase flex items-center gap-0.5" data-testid={`deadline-badge-${ann.id}`}>
                            <Clock size={9} /> Deadline
                          </span>
                        )}
                        <span className="text-xs font-semibold text-slate-800 truncate">{ann.title}</span>
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2 [overflow-wrap:anywhere]" data-testid={`notification-message-${ann.id}`}>{ann.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{ann.created_by_name} · {timeAgo(ann.created_at)}</p>
                    </div>
                    {(isManagement || ann.created_by === user?.id) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(ann.id); }}
                        className="text-slate-300 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                        data-testid={`delete-announcement-${ann.id}`}
                        aria-label={ann.system_kind === 'deadline' ? 'Dismiss deadline notification' : 'Delete notification'}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
